import { useCallback, useState } from "react";
import { DMAutomation, DMAutomationTrigger } from "@markit/common.types";
import { Colors } from "../../../../utils/colors";
import { BackButton } from "../../../Buttons/BackButton";
import { HorizontalDivider } from "../../../Dividers/HorizontalDivider";
import RectangleButton from "../../../Buttons/RectangleButton";
import DMAutomationTriggersSection from "./DMAutomationTriggersSection";
import SelectAutomationTriggerModal from "./SelectAutomationTriggerModal";
import LargePopupModalContainer from "../../../Containers/LargePopupModalContainer";
import { Icon } from "@iconify/react";
import DMAutomationCreateTrigger from "./DMAutomationCreateTrigger";
import { getNumDMAutomationTriggers } from "../../../../utils/dmAutomationUtils";
import AlertContainer from "../../../Containers/AlertContainer";
import { getAccountState } from "../../../../redux/slices/accountSlice";
import { useSelector } from "react-redux";

type DMAutomationAllTriggersPanelProps = {
  dmAutomation: DMAutomation;
  dmAutomationTriggers: DMAutomationTrigger[];
  onEditTrigger: (trigger: DMAutomationTrigger) => void;
  backOnPress: () => void;
};

const DMAutomationAllTriggersPanel = (
  props: DMAutomationAllTriggersPanelProps
) => {
  const { dmAutomation, dmAutomationTriggers, onEditTrigger, backOnPress } =
    props;
  const { uid } = useSelector(getAccountState).account.accountData;
  const [showSelectModal, setShowSelectModal] = useState(false);
  const [newTrigger, setNewTrigger] = useState<DMAutomationTrigger | undefined>(
    undefined
  );
  const [showAlert, setShowAlert] = useState<string | undefined>(undefined);

  const addTriggerOnPress = useCallback(async () => {
    const numTriggers = await getNumDMAutomationTriggers(uid, dmAutomation.id);
    if (numTriggers > 50) {
      setShowAlert(
        "You have the maximum number of triggers for this automation."
      );
      return;
    }
    setShowSelectModal(true);
  }, [uid, dmAutomation.id]);

  return (
    <>
      <LargePopupModalContainer
        showModal={true}
        closeOnOutsidePress={!newTrigger ? backOnPress : undefined}
        containerStyles={{ padding: 0, height: "100%", overflowY: "hidden" }}
        valueComp={
          <div
            className="ColumnNormal"
            style={{ height: "100%", backgroundColor: Colors.WHITE }}
          >
            <div
              className="AlignedRowSpaced"
              style={{ flexShrink: 0, height: 58, paddingInline: 14 }}
            >
              <div className="AlignedRow" style={{ gap: 10 }}>
                <BackButton onPress={backOnPress} iconName="ion:chevron-back" />
                <span className="sectionTitle">
                  All Triggers ({dmAutomationTriggers.length})
                </span>
              </div>
              <RectangleButton
                buttonLabel="New"
                iconRight={
                  <Icon
                    icon="ion:add-circle"
                    height={18}
                    style={{ color: Colors.WHITE }}
                  />
                }
                onPress={addTriggerOnPress}
                altColor={Colors.BLACK}
                altTextColor={Colors.WHITE}
                altBorderRadius={100}
                containerStyles={{
                  padding: "10px 14px",
                  width: "fit-content",
                }}
              />
            </div>
            <HorizontalDivider altMargin={0} />
            <div
              className="ColumnNormal HideScrollbar"
              style={{
                flex: 1,
                overflowY: "auto",
                minHeight: 0,
                padding: 14,
                gap: 14,
              }}
            >
              <DMAutomationTriggersSection
                dmAutomation={dmAutomation}
                dmAutomationTriggers={dmAutomationTriggers}
                isEditing
                onEditTrigger={onEditTrigger}
                showAll
              />
            </div>
            {showSelectModal ? (
              <SelectAutomationTriggerModal
                showModal={showSelectModal}
                setShowModal={setShowSelectModal}
                existingDmAutomation={dmAutomation}
                onSelectTrigger={(trigger) => setNewTrigger(trigger)}
              />
            ) : null}
          </div>
        }
      />
      {newTrigger ? (
        <LargePopupModalContainer
          showModal={true}
          containerStyles={{ padding: 0, height: "100%" }}
          valueComp={
            <DMAutomationCreateTrigger
              triggerItem={newTrigger}
              setTriggerItem={setNewTrigger}
              dmAutomation={dmAutomation}
              selectTriggerItem={() => setNewTrigger(undefined)}
              deleteTriggerOnPress={() => setNewTrigger(undefined)}
            />
          }
        />
      ) : null}
      {showAlert ? (
        <AlertContainer
          headerComp={showAlert}
          subHeaderComp={undefined}
          closeModal={() => setShowAlert("")}
          hideModal={showAlert === ""}
        />
      ) : null}
    </>
  );
};

export default DMAutomationAllTriggersPanel;
